import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT, inject, Injectable, signal } from '@angular/core';
import { ContextService } from '@services/context';
import { MIN_DESKTOP } from '@shared/constants';
import { LogsService } from '@shared/services/logs';
import { breakpoints } from '@shared/services/responsive/config';
import { BehaviorSubject, debounceTime, Observable, ReplaySubject } from 'rxjs';
import { tap } from 'rxjs/operators';

export type BreakpointAvailable = 'mobile' | 'desktop';

@Injectable({ providedIn: 'root' })
export class ResponsiveService {
  readonly breakpoints = signal<BreakpointAvailable>('mobile');
  readonly previousBreakpoints = signal<BreakpointAvailable>(null!);
  readonly devicePixelRatio = inject(DOCUMENT).defaultView?.devicePixelRatio ?? 1;
  private readonly isMobileStore$: ReplaySubject<boolean>;
  private readonly viewportWidth: BehaviorSubject<number>;
  private isMobileFlag = false;
  readonly #window = inject(DOCUMENT).defaultView as Window;
  #displayNameMap = new Map<string, BreakpointAvailable>([
    [breakpoints.mobile, 'mobile'],
    [breakpoints.desktop, 'desktop'],
  ]);

  readonly #contextService = inject(ContextService);
  readonly #logsService = inject(LogsService);

  constructor() {
    this.isMobileStore$ = new ReplaySubject<boolean>(1);
    this.viewportWidth = new BehaviorSubject<number>(this.#window.innerWidth);
    inject(BreakpointObserver)
      .observe([breakpoints.mobile, breakpoints.desktop])
      .pipe(
        tap((result) => {
          for (const query of Object.keys(result.breakpoints)) {
            if (result.breakpoints[query]) {
              this.previousBreakpoints.set(this.breakpoints());
              this.breakpoints.set(this.#displayNameMap.get(query) ?? 'mobile');
              this.#logsService.debug('ResponsiveService', `The breakpoint has been set on "${this.breakpoints()}"`);
            }
          }
        })
      )
      .subscribe();
  }

  public getWidth$(): Observable<number> {
    return this.viewportWidth.asObservable().pipe(debounceTime(300));
  }

  public setIsMobile(isMobile: boolean): void {
    this.isMobileStore$.next(isMobile);
  }

  public onInit(): void {
    let isMobile = this.#window.innerWidth < MIN_DESKTOP;
    if (this.#contextService.isServer()) {
      isMobile = true;
    }
    this.isMobileFlag = isMobile;
    this.setIsMobile(isMobile);
  }

  public onWindowResize(): void {
    const width = this.#window.innerWidth;
    this.viewportWidth.next(width);
    if (width < MIN_DESKTOP && !this.isMobileFlag) {
      this.isMobileFlag = true;
      this.setIsMobile(true);
    } else if (width >= MIN_DESKTOP && this.isMobileFlag) {
      this.isMobileFlag = false;
      this.setIsMobile(false);
    }
  }
}
